import React, { useCallback, useMemo, useRef, useState } from "react";
import _ from "lodash";
import { useSearchPageContext } from "../Context";
import { buildUri, PROVIDER_CONNECTION_TYPE, ROUTE, WaterProvider } from "../misc";
import ReactResizeDetector from "react-resize-detector";

import { Link } from "react-router-dom";

import styles from "./ProviderList.module.scss";
import { event as providerListClickEvent } from "../GA/ExploreProviders/ProviderListClick";

const MAX_COLUMN = 4;
const CHUNK_LENGTH_MIN = 1;

const LProviderList = ({ providers }: { providers: WaterProvider[] }) => {
    const ref = useRef<HTMLDivElement>();
    const maxColumn = useRef<number>(MAX_COLUMN);
    const refreshControlState = useState<{}>();
    const setRefresh = refreshControlState?.[1];

    const _providers = useMemo(() => {
        return _.sortBy(providers, doc => doc.name ?? "");
    }, [providers]);
    // providers ?? [];

    const bulkProviders = useMemo(() => {
        return _providers?.filter(d => d.connectionSizeCategory === PROVIDER_CONNECTION_TYPE.bulk);
    }, [_providers]);

    const nonbulkProviders = useMemo(() => {
        return _providers?.filter(d => d.connectionSizeCategory !== PROVIDER_CONNECTION_TYPE.bulk);
    }, [_providers]);

    // const ascOrderNameDocs = _.sortBy(providers, doc => doc.name ?? "");
    const bulkProviderLabel = "Bulk water providers";
    const groupToDocs = _.groupBy(nonbulkProviders, doc => ((doc.name ?? "")?.[0] ?? "").toUpperCase());
    const ascOrderGroups =
        bulkProviders?.length > 0
            ? [..._.sortBy(_.keys(groupToDocs)), bulkProviderLabel]
            : [..._.sortBy(_.keys(groupToDocs))];
    bulkProviders?.length > 0 && (groupToDocs[bulkProviderLabel] = bulkProviders);

    const columnCount = _.min([
        _.ceil(ascOrderGroups.length / CHUNK_LENGTH_MIN),
        _.ceil(ascOrderGroups.length / _.ceil(ascOrderGroups.length / maxColumn.current)),
        maxColumn.current
    ]);
    const chunkLength = _.ceil(ascOrderGroups.length / columnCount);
    const chunks = _.chunk(ascOrderGroups, chunkLength);

    const onResize = useCallback(() => {
        const current = ref.current;
        if (!current) return;

        if (current.clientWidth >= 992) {
            maxColumn.current = MAX_COLUMN;
        } else if (current.clientWidth >= 540) {
            maxColumn.current = 2;
        } else {
            maxColumn.current = 1;
        }

        setRefresh({});
    }, [setRefresh]);

    if (!providers) return <label>Select options above to find providers</label>;
    if (!providers.length) return <div>No results</div>;

    return (
        <div className="row provider-list" ref={ref}>
            {new Array(columnCount)
                .fill(0)
                .map((__, index) => index)
                .map(columnIndex => {
                    return (
                        <div className={`col-${12 / columnCount}`} key={columnIndex}>
                            {(chunks[columnIndex] ?? [])?.map((letter, index) => {
                                return (
                                    <React.Fragment key={index}>
                                        <h5 className={styles.searchResultHeader}>{letter}</h5>
                                        <div className={styles.searchResultEntry}>
                                            {(groupToDocs[letter] ?? [])?.map(({ name, adminId }, adminIndex) => {
                                                return (
                                                    <div key={adminIndex}>
                                                        <Link
                                                            to={buildUri(
                                                                ROUTE.PROVIDER_INFO,
                                                                {},
                                                                {
                                                                    id: adminId
                                                                }
                                                            )}
                                                            onClick={() => {
                                                                providerListClickEvent(name);
                                                            }}
                                                        >
                                                            {name}
                                                        </Link>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </React.Fragment>
                                );
                            })}
                        </div>
                    );
                })}

            <ReactResizeDetector handleWidth={true} onResize={onResize} />
        </div>
    );
};

const MemorisedProviderList = React.memo(LProviderList, (prev, next) => {
    return prev?.providers === next?.providers;
});

export const ProviderList = () => {
    const { searchResult } = useSearchPageContext();
    const { result: providers, loading } = searchResult ?? {};

    return (
        <>
            <h3 className={styles.providerListHeader}>
                Select from list
                {loading && ` (Loading ...)`}
            </h3>
            <MemorisedProviderList providers={providers} />
        </>
    );
};
